<template>
  <vxe-table
    v-if="!isMobile"
    class="trigger-table"
    empty-text="No data"
    border="none"
    :data="triggerDataList"
    :cell-class-name="cellClassName"
    :sort-config="{ sortMethod: customSortMethod }"
    :scroll-y="{ enabled: false }"
  >
    <vxe-column
      field="trigger"
      title="Prefix"
      width="45%"
      sortable
    ></vxe-column>
    <vxe-column
      field="status"
      title="Status"
      width="30%"
      sortable
      sort-by="status"
      :filters="[
        { label: 'Clean', value: 'Clean' },
        { label: 'Fired', value: 'Fired' },
        { label: 'Firing', value: 'Firing' },
        { label: 'Snoozed', value: 'Snoozed' }
      ]"
      :filter-multiple="false"
    ></vxe-column>
    <vxe-column title="History" width="25%">
      <template #default="{ row }">
        <span class="view-history" @click="viewHistory(row.trigger)"
          >{{ row.status !== 'Clean' ? 'View history' : '' }}
        </span>
      </template>
    </vxe-column>
  </vxe-table>
  <div v-else class="mobile">
    <div class="icon-group-and-search">
      <div class="icon-group">
        <fa
          icon="sort-amount-down"
          class="icon"
          :class="{ active: isShowSortTool }"
          @click="toggleIsShowSortTool"
        ></fa>
        <fa
          icon="filter"
          class="icon"
          :class="{ active: isShowFilterTool }"
          @click="toggleIsShowFilterTool"
        ></fa>
      </div>
    </div>
    <div v-if="isShowSortTool" class="tool">
      <SortAndFilterTool
        :toolConfig="sortToolConfig"
        @onToolChange="handleSortToolChange"
      ></SortAndFilterTool>
    </div>
    <div v-if="isShowFilterTool" class="tool">
      <SortAndFilterTool
        :toolConfig="filterToolConfig"
        @onToolChange="handleFilterToolChange"
      ></SortAndFilterTool>
    </div>
    <ul>
      <li v-for="(triggerData, index) in processedTriggerDataList" :key="index">
        <div class="wrapper">
          <span>Trigger</span>
          <span>{{ triggerData.trigger }}</span>
        </div>
        <div class="wrapper">
          <span>Status</span>
          <span :class="[getStatusClassName(triggerData.status)]">{{
            triggerData.status
          }}</span>
        </div>
        <div class="wrapper history">
          <span>History</span>
          <span
            @click="viewHistory(triggerData.trigger)"
            class="view-history"
            >{{ triggerData.status !== 'Clean' ? 'View history' : '' }}</span
          >
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SortAndFilterTool from '../alertTableParts/SortAndFilterTool.vue';
import {
  TRIGGER_STATUS,
  TRIGGER_SORT_OPTION,
  TRIGGER_FILTER_OPTION
} from '../../../../constants/alertsConstant.js';
import {
  sortPrefixByAscending,
  sortPrefixByDescending
} from '../../../../utils/ipAddressHelpers';

export default {
  name: 'TriggerTable',
  components: { SortAndFilterTool },
  props: ['triggerDataList'],
  data() {
    return {
      isShowSortTool: false,
      isShowFilterTool: false,
      sortType: 0,
      filterType: 0,
      sortToolConfig: {
        placeholder: 'Sort',
        toolList: [
          { id: TRIGGER_SORT_OPTION.NONE, text: 'None' },
          {
            id: TRIGGER_SORT_OPTION.TRIGGER_LOWEST_FIRST,
            text: 'Trigger: lowest first'
          },
          {
            id: TRIGGER_SORT_OPTION.TRIGGER_HIGHEST_FIRST,
            text: 'Trigger: highest first'
          }
        ]
      },
      filterToolConfig: {
        placeholder: 'Filter',
        toolList: [
          { id: TRIGGER_FILTER_OPTION.NONE, text: 'None' },
          {
            id: TRIGGER_FILTER_OPTION.TRIGGERING,
            text: 'Triggering'
          },
          {
            id: TRIGGER_FILTER_OPTION.TRIGGERED,
            text: 'Triggered'
          },
          {
            id: TRIGGER_FILTER_OPTION.CLEAN,
            text: 'Clean'
          }
        ]
      }
    };
  },
  computed: {
    ...mapGetters(['isMobile']),
    processedTriggerDataList() {
      let list = [];

      switch (this.filterType) {
        case TRIGGER_FILTER_OPTION.NONE:
          list = this.triggerDataList;
          break;
        case TRIGGER_FILTER_OPTION.TRIGGERING:
          list = this.triggerDataList.filter(
            trigger => trigger.status === TRIGGER_STATUS.TRIGGERING
          );
          break;
        case TRIGGER_FILTER_OPTION.TRIGGERED:
          list = this.triggerDataList.filter(
            trigger => trigger.status === TRIGGER_STATUS.TRIGGERED
          );
          break;
        case TRIGGER_FILTER_OPTION.CLEAN:
          list = this.triggerDataList.filter(
            trigger => trigger.status === TRIGGER_STATUS.CLEAN
          );
          break;
        default:
          list = this.triggerDataList;
          break;
      }

      // deep clone list as list.sort will change the origin list
      list = JSON.parse(JSON.stringify(list));

      switch (this.sortType) {
        case TRIGGER_SORT_OPTION.NONE:
          break;
        case TRIGGER_SORT_OPTION.TRIGGER_HIGHEST_FIRST:
          list.sort((a, b) => sortPrefixByDescending(a.trigger, b.trigger));
          break;
        case TRIGGER_SORT_OPTION.TRIGGER_LOWEST_FIRST:
          list.sort((a, b) => sortPrefixByAscending(a.trigger, b.trigger));
          break;
        default:
          break;
      }

      return list;
    }
  },
  methods: {
    cellClassName({ row, rowIndex, column, columnIndex }) {
      if (column.property === 'status') {
        let addedClassName = '';
        switch (row.status) {
          case TRIGGER_STATUS.TRIGGERING:
            addedClassName = 'prefix-icon prefix-icon--table red-dot';
            break;
          case TRIGGER_STATUS.TRIGGERED:
            addedClassName = 'prefix-icon prefix-icon--table red-circle';
            break;
          case TRIGGER_STATUS.CLEAN:
            addedClassName = 'prefix-icon prefix-icon--table green-dot';
            break;
          default:
            addedClassName = '';
            break;
        }
        return addedClassName;
      }
    },
    getStatusClassName(status) {
      let addedClassName = '';
      switch (status) {
        case TRIGGER_STATUS.TRIGGERING:
          addedClassName = 'prefix-icon red-dot';
          break;
        case TRIGGER_STATUS.TRIGGERED:
          addedClassName = 'prefix-icon red-circle';
          break;
        case TRIGGER_STATUS.CLEAN:
          addedClassName = 'prefix-icon green-dot';
          break;
        default:
          addedClassName = '';
          break;
      }
      return addedClassName;
    },
    customSortMethod({ data, sortList }) {
      const { property, order } = sortList[0];

      let list = [];
      if (property === 'trigger') {
        if (order === 'asc') {
          list = data.sort((a, b) =>
            sortPrefixByAscending(a.trigger, b.trigger)
          );
        } else if (order === 'desc') {
          list = data.sort((a, b) =>
            sortPrefixByDescending(a.trigger, b.trigger)
          );
        } else {
          list = data;
        }
      } else if (property === 'status') {
        if (order === 'asc') {
          list = data.sort((a, b) => a.statusOrder - b.statusOrder);
        } else if (order === 'desc') {
          list = data.sort((a, b) => b.statusOrder - a.statusOrder);
        } else {
          list = data;
        }
      }

      return list;
    },
    viewHistory(val) {
      this.$emit('onViewTriggerHistory', val);
    },
    toggleIsShowSortTool() {
      this.isShowSortTool = !this.isShowSortTool;
      if (!this.isShowSortTool) this.sortType = TRIGGER_SORT_OPTION.NONE;
    },
    toggleIsShowFilterTool() {
      this.isShowFilterTool = !this.isShowFilterTool;
      if (!this.isShowFilterTool) this.filterType = TRIGGER_FILTER_OPTION.NONE;
    },
    handleSortToolChange(val) {
      this.sortType = val;
    },
    handleFilterToolChange(val) {
      this.filterType = val;
    }
  }
};
</script>

<style lang="scss" scoped>
.trigger-table {
  ::v-deep .vxe-header--row {
    background-color: #ffffff !important;
    background-image: none !important;
  }

  ::v-deep td.vxe-body--column {
    background-image: none;
  }

  .view-history {
    padding: 0.5rem 0;
    cursor: pointer;
    color: $text-light-blue;
    white-space: initial;
  }
}

.mobile {
  ul {
    list-style: none;
  }

  li {
    border-bottom: 1px solid #ddd;
    padding-top: 1rem;
  }

  .wrapper {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    margin-bottom: 1rem;
  }

  .wrapper span:nth-child(1) {
    color: $icon-grey;
    font-weight: 600;
  }

  .view-history {
    cursor: pointer;
    color: $text-light-blue;
    white-space: initial;
  }

  .icon-group-and-search {
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;

    .icon {
      padding: 0.5rem;
      margin-right: 0.5rem;
      width: 2rem;
      height: 2rem;
      color: $icon-grey;
    }

    .active {
      color: $text-light-blue;
    }
  }

  .tool {
    padding-bottom: 0.5rem;
  }
}
</style>
